import { forwardRef, memo, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import map from 'lodash/map';
import sum from 'lodash/sum';
import max from 'lodash/max';
import size from 'lodash/size';
import round from 'lodash/round';
import sortBy from 'lodash/sortBy';
import transform from 'lodash/transform';
import isNil from 'lodash/isNil';
import toSafeInteger from 'lodash/toSafeInteger';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
// Material Icon imports
import ArrowBackIosRounded from '@mui/icons-material/ArrowBackIosRounded';
// Skillmore UI Components
import { fontWeightMedium } from '@empathco/ui-components/src/styles/themeOptions';
import { mapChunks } from '@empathco/ui-components/src/helpers/intl';
import { spacing } from '@empathco/ui-components/src/helpers/styles';
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import useCombinedRefs from '@empathco/ui-components/src/hooks/useCombinedRefs';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
import Scrollable from '@empathco/ui-components/src/elements/Scrollable';
// local imports
import { DASkillsInsightCounts, SkillInsightsCourse } from '../graphql/customTypes';
import { SKILL_LEVEL_FIRST, SKILL_LEVEL_MAX } from '../models/skill';
import { barColorCls, requiredColorCls } from '../constants/chartColors';
import useCustomerSettings from '../config/customer';
// SCSS imports
import {
  tooltipHeaderWithHint, tooltipRow, tooltipFirst, tooltipBars, tooltipBarContainer,
  tooltipLine, tooltipBar, tooltipBarLabel, tooltipInfo, tooltipHint
} from './TopChartGapsPopup.module.scss';

const approzimateTooltipSize = [420, 310] as const;
const tooltipMargin = [12, 8] as const;

const adjustCoordinate = (point: number, offset: number, margin: number, elSize: number, viewSize: number) => {
  let cc = point + offset;
  if (cc + elSize > viewSize - margin) {
    const cc1 = point - elSize - offset;
    if (cc1 >= margin) cc = cc1;
    else {
      cc = viewSize - elSize - margin;
      if (cc < margin) cc = margin;
    }
  }
  return cc;
}

// adjust position to keep within viewport
export const adjustTooltipPosition = (point: number[], viewSize: number[], _domRect?: DOMRect): number[] | undefined =>
  // Enable if we want to freeze the popup position when mouse cursor is inside the tooltip popup:
  // domRect && (
  //   domRect.left <= point[0] && point[0] <= domRect.right &&
  //   domRect.top <= point[1] && point[1] <= domRect.bottom
  // ) ? null :
  [
    adjustCoordinate(point[0], tooltipMargin[0], tooltipMargin[0], approzimateTooltipSize[0], viewSize[0]),
    adjustCoordinate(point[1], tooltipMargin[1], tooltipMargin[1], approzimateTooltipSize[1], viewSize[1])
  ];

const sxFontWeightMedium: SxProps<Theme> = { fontWeight: fontWeightMedium } as const;
const sxSkillName: SxProps<Theme> = { flex: '1 0 0' } as const;
const sxCoursesList: SxProps<Theme> = { px: 1, py: 0.5 } as const;
const sxBackButton: SxProps<Theme> = { flex: '0 0 auto' } as const;
const sxCourseLevel: SxProps<Theme> = { mb: 0.5 } as const;
const sxEmployeesCount: SxProps<Theme> = { px: 1 } as const;

type TooltipSkillLevelDetailsProps = {
  readonly fixed?: boolean;
  readonly seriesIndex: number;
  readonly expectedLevel: number;
  readonly count: number;
  readonly requiredCount: number;
  readonly maxValue: number;
  readonly coursesCount: number;
  readonly onCoursesLineClick?: () => void;
};

const TooltipSkillLevelDetails = forwardRef<HTMLDivElement, TooltipSkillLevelDetailsProps>(({
  fixed,
  seriesIndex,
  expectedLevel,
  count,
  requiredCount,
  maxValue,
  coursesCount,
  onCoursesLineClick
}, ref) => {
  const { formatMessage, formatNumber } = useIntl();
  return (
    <div ref={ref} className={tooltipRow}>
      <div className={tooltipFirst}>
        <span>
          {formatMessage({ id: 'common.skill_level.text' }, { level: expectedLevel + SKILL_LEVEL_FIRST })}
        </span>
        <Box component="span" className={tooltipInfo}>
          {formatMessage({ id: 'hr.dashboard.radar.courses' }, {
            count: coursesCount,
            // eslint-disable-next-line react/no-unstable-nested-components
            link: (chunks?: ReactNode | null): ReactNode => fixed ? (
              <StandardLink onClick={onCoursesLineClick}>
                {mapChunks(chunks)}
              </StandardLink>
            ) : (
              <Box component="span" sx={sxFontWeightMedium}>
                {mapChunks(chunks)}
              </Box>
            )
          })}
        </Box>
      </div>
      <div className={tooltipBars}>
        <span className={tooltipBarContainer}>
          <span className={tooltipLine}>
            <span
                className={`${tooltipBar} ${
                  barColorCls[seriesIndex % barColorCls.length]?.[expectedLevel] || ''} bar-width-${
                  round(80 * count / maxValue) + 1}`}
            >
              {null}
            </span>
            <span className={tooltipBarLabel}>
              {formatNumber(count)}
            </span>
          </span>
          <span className={tooltipLine}>
            <span
                className={`${tooltipBar} ${requiredColorCls[expectedLevel] || ''} bar-width-${
                  round(80 * requiredCount / maxValue) + 1}`}
            >
              {null}
            </span>
            <span className={tooltipBarLabel}>
              {formatNumber(requiredCount)}
            </span>
          </span>
        </span>
      </div>
    </div>
  );
});
TooltipSkillLevelDetails.displayName = 'TooltipSkillLevelDetails';

type TopChartGapsPopupProps = {
  readonly path?: string;
  readonly visible?: boolean;
  readonly fixed?: boolean;
  readonly seriesIndex: number;
  readonly content?: DASkillsInsightCounts;
  readonly position: { left: number; top: number; };
  readonly showCourses?: boolean;
  readonly onCoursesClick?: (show: boolean) => void;
}

const TopChartGapsPopup = forwardRef<HTMLDivElement, TopChartGapsPopupProps>(({
  path,
  visible,
  fixed,
  seriesIndex,
  content,
  position,
  showCourses,
  onCoursesClick
}, ref) => {
  const { formatMessage } = useIntl();
  const { getCourseUrl } = useCustomerSettings();
  const theme = useTheme();

  const innerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useCombinedRefs<HTMLDivElement>(ref, innerRef);
  const [coursesPopupSize, setCoursesPopupSize] = useState<number[] | null>(null);

  const { abbr } = content || {};
  const skillLink = useMemo(() => abbr && path ? injectParams(path, { skill_id: abbr }) : undefined, [abbr, path]);

  const handleCoursesLineClick = useCallback(() => {
    setCoursesPopupSize(innerRef.current ? [
      innerRef.current.clientWidth + theme.shape.borderWidth * 2 + 1,
      innerRef.current.clientHeight + theme.shape.borderWidth * 2 + 1
    ] : null);
    onCoursesClick?.(true);
  }, [onCoursesClick, theme.shape.borderWidth]);

  const handleBackClick = useCallback(() => {
    onCoursesClick?.(false);
  }, [onCoursesClick]);

  const popupSx: SxProps<Theme> = useMemo(() => {
    const transitionDuration = 0.4;
    const transitionCurve = 'cubic-bezier(0.23,1,0.32,1)';
    const transitionOption1 = ` ${transitionDuration}s ${transitionCurve}`;
    const transitionOption2 = ` ${transitionDuration / 2}s ${transitionCurve}`;
    return {
      position: 'absolute',
      left: position.left,
      top: position.top,
      ...showCourses && coursesPopupSize ? {
        width: `${coursesPopupSize[0]}px`,
        height: `${coursesPopupSize[1]}px`
      } : {},
      zIndex: 10,
      padding: 1,
      border: `${theme.shape.borderWidth}px solid ${theme.palette.misc.selectedBorder}`,
      borderRadius: theme.shape.tinyBorderRadius,
      background: fixed ? theme.palette.background.paper : theme.palette.background.tooltipLight,
      boxShadow: theme.shadows[5],
      transition: `opacity${transitionOption2},visibility${transitionOption2},left${
        transitionOption1},top${transitionOption1}`,
      ...fixed ? {} : { pointerEvents: 'none' } // allows mouse events to pass through to the chart
    };
  }, [position, fixed, showCourses, coursesPopupSize, theme]);

  const headerSx = useMemo(() => {
    const spacing1 = `${spacing(1)}px`;
    return {
      padding: showCourses
        ? `${spacing(0.25)}px ${spacing1} 0 0`
        : `${spacing1} ${spacing1} 0 ${spacing1}`
    };
  }, [showCourses]);

  const {
    title, courses, level1, level2, level3, level4,
    required_level_1, required_level_2, required_level_3, required_level_4
  } = content || {};

  const levels = useMemo(() => [level1 || 0, level2 || 0, level3 || 0, level4 || 0], [level1, level2, level3, level4]);
  const employeesValues = useMemo(() => ({ count: sum(levels) }), [levels]);

  const levelCourses = useMemo(() => map(
    transform(courses || [], (result, course) => {
      const lvl = isNil(course.level) ? SKILL_LEVEL_FIRST : toSafeInteger(course.level);
      if (lvl >= SKILL_LEVEL_FIRST && lvl <= SKILL_LEVEL_MAX) {
        result[lvl - SKILL_LEVEL_FIRST].push(course);
      }
    }, map(levels, () => []) as SkillInsightsCourse[][]),
    (coursesList) => sortBy(coursesList, ['title', 'abbr', 'code', 'id'])
  ), [courses, levels]);

  const required = [required_level_1 || 0, required_level_2 || 0, required_level_3 || 0, required_level_4 || 0];

  // A skill nobody holds is the widest gap this chart ranks by, so required levels and courses
  // are content in their own right; only a popup with none of the three has nothing to show.
  if (!visible || !content || (!sum(levels) && !sum(required) && size(courses) < 1)) return null;

  const maxValue = max([...levels, ...required]) || 1;

  return (
    <Box ref={tooltipRef} sx={popupSx}>
      <Box
          className={tooltipHeaderWithHint}
          sx={headerSx}
      >
        {showCourses ? (
          <IconButton
              size="medium"
              color="primary"
              onClick={handleBackClick}
              sx={sxBackButton}
          >
            <ArrowBackIosRounded color="inherit" fontSize="medium"/>
          </IconButton>
        ) : undefined}
        <Box component="span" sx={sxSkillName}>
          {!showCourses && fixed && skillLink ? (
            <StandardLink to={skillLink}>
              {title}
            </StandardLink>
          ) : title}
        </Box>
      </Box>
      <Typography variant="body1" sx={sxEmployeesCount}>
        <FormattedMessage id="hr.dashboard.frequent_skill_gap.employees" values={employeesValues}/>
      </Typography>
      <span className={tooltipHint}>
        {formatMessage({ id: 'hr.dashboard.radar.click' }, { fixed, courses: size(courses) >= 1 })}
      </span>
      {showCourses ? (
        <Scrollable sx={sxCoursesList} autofit>
          {map(levelCourses, (coursesList, expectedLevel) => size(coursesList) >= 1 ? (
            <Box key={expectedLevel} mt={1}>
              <Typography variant="h5" sx={sxCourseLevel}>
                <FormattedMessage id="hr.dashboard.radar.course_level" values={{ level: expectedLevel + 1 }}/>
              </Typography>
              {map(coursesList, ({ id, title: courseTitle, code, external_url }) => {
                const href = external_url || getCourseUrl(code);
                return (
                  <Box key={id} mb={0.5} ml={2}>
                    {href ? (
                      <StandardLink href={href}>
                        {courseTitle}
                      </StandardLink>
                    ) : courseTitle}
                  </Box>
                );
              })}
            </Box>
          ) : undefined)}
        </Scrollable>
      ) : map(levels, (count, expectedLevel) => (
        <TooltipSkillLevelDetails
            key={expectedLevel}
            fixed={fixed}
            seriesIndex={seriesIndex}
            maxValue={maxValue}
            expectedLevel={expectedLevel}
            count={count}
            requiredCount={required[expectedLevel]}
            coursesCount={size(levelCourses[expectedLevel])}
            onCoursesLineClick={content ? handleCoursesLineClick : undefined}
        />
      ))}
    </Box>
  );
});
TopChartGapsPopup.displayName = 'TopChartGapsPopup';

export default memo(TopChartGapsPopup);
